import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { differenceInDays, parseISO } from 'date-fns';

export const exportRoadmapToPDF = (roadmapData, t) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  let yPosition = margin;
  let pageNumber = 1;

  // Color Palette
  const colors = {
    primary: [63, 81, 181],
    secondary: [156, 39, 176],
    success: [46, 125, 50],
    warning: [237, 108, 2],
    error: [211, 47, 47],
    info: [2, 136, 209],
    grey: [158, 158, 158],
    lightGrey: [245, 245, 245],
    white: [255, 255, 255]
  };

  // Helper function to draw progress bar
  const drawProgressBar = (x, y, width, height, progress, label = '') => {
    // Background
    doc.setFillColor(...colors.lightGrey);
    doc.rect(x, y, width, height, 'F');
    
    // Progress fill
    const progressWidth = (width * progress) / 100;
    const color = progress >= 75 ? colors.success : progress >= 50 ? colors.info : progress >= 25 ? colors.warning : colors.error;
    doc.setFillColor(...color);
    doc.rect(x, y, progressWidth, height, 'F');
    
    // Border
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.rect(x, y, width, height, 'S');
    
    // Label
    if (label) {
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(`${progress}%`, x + width + 2, y + height - 1);
    }
  };

  // Helper function to draw SWOT matrix
  const drawSWOTMatrix = (swot, startY) => {
    const quadrantWidth = (pageWidth - 2 * margin) / 2 - 2;
    const quadrantHeight = 35;
    const gap = 4;

    const quadrants = [
      { title: 'Strengths', items: swot.strengths || [], color: colors.success, x: margin, y: startY },
      { title: 'Weaknesses', items: swot.weaknesses || [], color: colors.error, x: margin + quadrantWidth + gap, y: startY },
      { title: 'Opportunities', items: swot.opportunities || [], color: colors.info, x: margin, y: startY + quadrantHeight + gap },
      { title: 'Threats', items: swot.threats || [], color: colors.warning, x: margin + quadrantWidth + gap, y: startY + quadrantHeight + gap }
    ];

    quadrants.forEach(quad => {
      // Header
      doc.setFillColor(...quad.color);
      doc.rect(quad.x, quad.y, quadrantWidth, 8, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.white);
      doc.text(quad.title, quad.x + 2, quad.y + 6);

      // Content area
      doc.setFillColor(250, 250, 250);
      doc.rect(quad.x, quad.y + 8, quadrantWidth, quadrantHeight - 8, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.rect(quad.x, quad.y, quadrantWidth, quadrantHeight, 'S');

      // Items
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      let itemY = quad.y + 14;
      quad.items.slice(0, 4).forEach(item => {
        const text = typeof item === 'object' ? item.text || '' : item;
        if (text) {
          const lines = doc.splitTextToSize(`- ${text}`, quadrantWidth - 4);
          doc.text(lines[0], quad.x + 2, itemY);
          itemY += 5;
        }
      });

      if (quad.items.length > 4) {
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...colors.grey);
        doc.text(`+${quad.items.length - 4} more...`, quad.x + 2, itemY);
      }
    });

    return startY + (quadrantHeight + gap) * 2 + 10;
  };

  // Helper function to draw timeline/Gantt
  const drawTimelineGantt = (phases, startDate, endDate, startY) => {
    if (!phases || phases.length === 0 || !startDate || !endDate) {
      return startY + 10;
    }

    const projectStart = parseISO(startDate);
    const projectEnd = parseISO(endDate);
    const totalDays = differenceInDays(projectEnd, projectStart);
    const chartWidth = pageWidth - 2 * margin - 50;
    const barHeight = 6;
    const rowHeight = 12;

    // Draw timeline axis
    doc.setDrawColor(...colors.grey);
    doc.setLineWidth(0.3);
    doc.line(margin + 50, startY, margin + 50 + chartWidth, startY);

    // Draw phases (max 8 to fit on page)
    phases.slice(0, 8).forEach((phase, index) => {
      const phaseY = startY + 5 + index * rowHeight;
      
      // Phase name
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const phaseName = phase.name && phase.name.length > 20 ? phase.name.substring(0, 17) + '...' : phase.name || 'Phase';
      doc.text(phaseName, margin, phaseY + 4);

      if (phase.start_date && phase.end_date) {
        const phaseStart = parseISO(phase.start_date);
        const phaseEnd = parseISO(phase.end_date);
        const startOffset = differenceInDays(phaseStart, projectStart);
        const duration = differenceInDays(phaseEnd, phaseStart);

        const barX = margin + 50 + (startOffset / totalDays) * chartWidth;
        const barWidth = (duration / totalDays) * chartWidth;

        // Phase bar
        const phaseColor = phase.is_critical ? colors.error : colors.primary;
        doc.setFillColor(...phaseColor);
        doc.rect(barX, phaseY, barWidth, barHeight, 'F');

        // Progress overlay
        if (phase.progress > 0) {
          doc.setFillColor(255, 255, 255, 0.3);
          doc.rect(barX, phaseY, (barWidth * phase.progress) / 100, barHeight, 'F');
        }

        // Border
        doc.setDrawColor(150, 150, 150);
        doc.setLineWidth(0.2);
        doc.rect(barX, phaseY, barWidth, barHeight, 'S');
      }
    });

    return startY + Math.min(phases.length, 8) * rowHeight + 15;
  };

  // Helper function to add page footer
  const addFooter = () => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.grey);
    doc.text(
      `${roadmapData.project_name || 'Roadmap'} | Page ${pageNumber}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    pageNumber++;
  };

  // Helper function to add new page if needed
  const checkAndAddPage = (requiredSpace = 20) => {
    if (yPosition + requiredSpace > pageHeight - margin - 10) {
      addFooter();
      doc.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Helper function to add section title with icon
  const addSectionTitle = (title, icon = '') => {
    checkAndAddPage(30);
    
    // Background bar
    doc.setFillColor(...colors.primary);
    doc.rect(margin, yPosition - 5, pageWidth - 2 * margin, 12, 'F');
    
    // Title text
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.white);
    doc.text(`${icon} ${title}`, margin + 5, yPosition + 3);
    
    yPosition += 15;
  };

  // Helper function to add subsection
  const addSubsection = (title, content) => {
    checkAndAddPage(25);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text(title, margin, yPosition);
    yPosition += 7;

    if (content) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      const lines = doc.splitTextToSize(content, pageWidth - 2 * margin);
      lines.forEach(line => {
        checkAndAddPage();
        doc.text(line, margin, yPosition);
        yPosition += 5;
      });
      yPosition += 3;
    }
  };

  // Helper function to add list items with icons
  const addListItems = (items, bulletChar = '-') => {
    if (!items || items.length === 0) {
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...colors.grey);
      doc.setFontSize(9);
      doc.text('None', margin + 5, yPosition);
      yPosition += 7;
      return;
    }

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    
    items.forEach(item => {
      checkAndAddPage();
      const text = typeof item === 'object' ? item.text || item.name || item.description || JSON.stringify(item) : item;
      const lines = doc.splitTextToSize(text, pageWidth - 2 * margin - 10);
      
      doc.setTextColor(...colors.primary);
      doc.text(bulletChar, margin + 2, yPosition);
      doc.setTextColor(0, 0, 0);
      
      lines.forEach((line, idx) => {
        checkAndAddPage();
        doc.text(line, margin + (idx === 0 ? 8 : 10), yPosition);
        yPosition += 5;
      });
    });
    yPosition += 3;
  };

  // ===== COVER PAGE =====
  // Gradient background effect (simulated with rectangles)
  for (let i = 0; i < 80; i++) {
    const blue = Math.floor(63 + (192 * i / 80));
    doc.setFillColor(blue, 81 + i, 181);
    doc.rect(0, i, pageWidth, 1, 'F');
  }
  
  // Title
  doc.setTextColor(...colors.white);
  doc.setFontSize(32);
  doc.setFont('helvetica', 'bold');
  doc.text('STRATEGIC ROADMAP', pageWidth / 2, 35, { align: 'center' });
  
  // Project name
  doc.setFontSize(20);
  doc.setFont('helvetica', 'normal');
  doc.text(roadmapData.project_name || 'Untitled Project', pageWidth / 2, 55, { align: 'center' });

  // Info cards
  yPosition = 100;
  const cardWidth = (pageWidth - 2 * margin - 6) / 3;
  
  // Type card
  doc.setFillColor(...colors.lightGrey);
  doc.roundedRect(margin, yPosition, cardWidth, 25, 2, 2, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.grey);
  doc.text('TYPE', margin + cardWidth / 2, yPosition + 8, { align: 'center' });
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text((roadmapData.project_type || 'N/A').toUpperCase(), margin + cardWidth / 2, yPosition + 18, { align: 'center' });

  // Created card
  doc.setFillColor(...colors.lightGrey);
  doc.roundedRect(margin + cardWidth + 3, yPosition, cardWidth, 25, 2, 2, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.grey);
  doc.text('CREATED', margin + cardWidth + 3 + cardWidth / 2, yPosition + 8, { align: 'center' });
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text(roadmapData.created_date || 'N/A', margin + cardWidth + 3 + cardWidth / 2, yPosition + 18, { align: 'center' });

  // Completion card with visual progress
  doc.setFillColor(...colors.lightGrey);
  doc.roundedRect(margin + 2 * (cardWidth + 3), yPosition, cardWidth, 25, 2, 2, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.grey);
  doc.text('COMPLETION', margin + 2 * (cardWidth + 3) + cardWidth / 2, yPosition + 8, { align: 'center' });
  
  const completion = roadmapData.completion_status || 0;
  drawProgressBar(margin + 2 * (cardWidth + 3) + 5, yPosition + 12, cardWidth - 10, 8, completion, true);

  yPosition = 140;

  // Executive Summary
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 60, 3, 3, 'F');
  doc.setDrawColor(...colors.primary);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 60, 3, 3, 'S');

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.primary);
  doc.text('Executive Summary', margin + 5, yPosition + 10);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  const summaryText = roadmapData.steps?.vision?.vision_statement || 
                      'This strategic roadmap outlines the vision, objectives, and action plan for achieving organizational goals.';
  const summaryLines = doc.splitTextToSize(summaryText, pageWidth - 2 * margin - 10);
  summaryLines.slice(0, 6).forEach((line, idx) => {
    doc.text(line, margin + 5, yPosition + 20 + idx * 5);
  });

  yPosition = 220;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...colors.grey);
  doc.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });

  addFooter();

  // ===== TABLE OF CONTENTS =====
  doc.addPage();
  yPosition = margin;
  
  addSectionTitle('TABLE OF CONTENTS', '');  const tocItems = [
    '1. Vision & Mission',
    '2. Situation Analysis (SWOT)',
    '3. Strategic Objectives',
    '4. Action Components',
    '5. Resources',
    '6. Timeline',
    '7. Risk Management',
    '8. Governance',
    '9. Performance Metrics',
    '10. Adaptation Strategy'
  ];

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  tocItems.forEach((item, index) => {
    checkAndAddPage();
    doc.setTextColor(0, 0, 0);
    doc.text(item, margin + 10, yPosition);
    
    // Dotted line
    doc.setDrawColor(...colors.grey);
    doc.setLineDash([1, 2]);
    doc.line(margin + 80, yPosition - 2, pageWidth - margin - 15, yPosition - 2);
    doc.setLineDash([]);
    
    doc.text(String(index + 2), pageWidth - margin - 10, yPosition);
    yPosition += 10;
  });

  addFooter();

  // ===== SECTION 1: VISION & MISSION =====
  doc.addPage();
  yPosition = margin;
  
  addSectionTitle('1. VISION & MISSION', '');

  const vision = roadmapData.steps?.vision || {};

  // Vision Statement Card
  if (vision.vision_statement) {
    doc.setFillColor(240, 248, 255);
    doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 30, 2, 2, 'F');
    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 30, 2, 2, 'S');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Vision Statement', margin + 5, yPosition + 8);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    const visionLines = doc.splitTextToSize(vision.vision_statement, pageWidth - 2 * margin - 10);
    visionLines.slice(0, 3).forEach((line, idx) => {
      doc.text(line, margin + 5, yPosition + 16 + idx * 5);
    });
    yPosition += 35;
  }

  // Mission Statement Card
  if (vision.mission_statement) {
    checkAndAddPage(35);
    doc.setFillColor(255, 248, 240);
    doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 30, 2, 2, 'F');
    doc.setDrawColor(...colors.warning);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 30, 2, 2, 'S');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.warning);
    doc.text('Mission Statement', margin + 5, yPosition + 8);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    const missionLines = doc.splitTextToSize(vision.mission_statement, pageWidth - 2 * margin - 10);
    missionLines.slice(0, 3).forEach((line, idx) => {
      doc.text(line, margin + 5, yPosition + 16 + idx * 5);
    });
    yPosition += 35;
  }

  checkAndAddPage(25);
  addSubsection('Purpose', vision.purpose);

  checkAndAddPage(30);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.primary);
  doc.text('Timeframe', margin, yPosition);
  yPosition += 7;
  
  if (vision.timeframe) {
    const timeframeLabels = {
      '1-2': '1-2 years',
      '3-5': '3-5 years',
      '5-10': '5-10 years',
      '10+': '10+ years'
    };
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(`Timeframe: ${timeframeLabels[vision.timeframe] || vision.timeframe}`, margin + 5, yPosition);
    yPosition += 10;
  }

  checkAndAddPage(20);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.primary);
  doc.text('Core Values', margin, yPosition);
  yPosition += 7;
  addListItems(vision.values, '-');

  // Stakeholders with visual cards
  if (vision.stakeholders && vision.stakeholders.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Key Stakeholders', margin, yPosition);
    yPosition += 7;

    vision.stakeholders.slice(0, 5).forEach(stakeholder => {
      checkAndAddPage(15);
      doc.setFillColor(...colors.lightGrey);
      doc.roundedRect(margin + 5, yPosition - 3, pageWidth - 2 * margin - 10, 10, 1, 1, 'F');
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`${stakeholder.name}`, margin + 8, yPosition + 3);
      
      if (stakeholder.role) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...colors.grey);
        doc.text(`Role: ${stakeholder.role}`, margin + 8, yPosition + 8);
      }
      
      yPosition += 15;
    });
  }

  addFooter();

  // ===== SECTION 2: SWOT ANALYSIS =====
  doc.addPage();
  yPosition = margin;
  
  addSectionTitle('2. SITUATION ANALYSIS (SWOT)', '');

  const swot = roadmapData.steps?.situation_analysis || {};
  
  checkAndAddPage(85);
  yPosition = drawSWOTMatrix(swot, yPosition);

  addFooter();

  // ===== SECTION 3: STRATEGIC OBJECTIVES =====
  doc.addPage();
  yPosition = margin;
  
  addSectionTitle('3. STRATEGIC OBJECTIVES', '');

  const objectives = roadmapData.steps?.strategic_objectives?.objectives || [];

  objectives.forEach((objective, index) => {
    checkAndAddPage(50);
    
    // Objective card
    doc.setFillColor(...colors.lightGrey);
    doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 8, 1, 1, 'F');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text(`Objective ${index + 1}: ${objective.name || 'Untitled'}`, margin + 3, yPosition + 6);
    yPosition += 12;

    if (objective.description) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const descLines = doc.splitTextToSize(objective.description, pageWidth - 2 * margin - 5);
      descLines.slice(0, 2).forEach(line => {
        checkAndAddPage();
        doc.text(line, margin + 3, yPosition);
        yPosition += 5;
      });
    }

    // Metadata row
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    let metaY = yPosition + 3;
    
    if (objective.priority) {
      const priorityColors = { high: colors.error, medium: colors.warning, low: colors.success };
      const color = priorityColors[objective.priority] || colors.grey;
      doc.setTextColor(...color);
      doc.text(`Priority: ${objective.priority.toUpperCase()}`, margin + 3, metaY);
    }
    
    if (objective.target_date) {
      doc.setTextColor(...colors.grey);
      doc.text(`Target: ${objective.target_date}`, margin + 50, metaY);
    }
    
    if (objective.owner) {
      doc.text(`Owner: ${objective.owner}`, margin + 100, metaY);
    }
    
    yPosition += 10;

    // Key Results with progress bars
    if (objective.key_results && objective.key_results.length > 0) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.secondary);
      doc.text('Key Results:', margin + 3, yPosition);
      yPosition += 6;

      objective.key_results.forEach(kr => {
        checkAndAddPage(12);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        const krText = kr.description || 'Key Result';
        doc.text(`- ${krText}`, margin + 5, yPosition);
        yPosition += 5;
        
        if (kr.progress !== undefined) {
          drawProgressBar(margin + 8, yPosition - 2, 60, 4, kr.progress, true);
          yPosition += 6;
        }
      });
    }

    yPosition += 8;
  });

  addFooter();

  // ===== SECTION 6: TIMELINE (Enhanced with Gantt) =====
  doc.addPage();
  yPosition = margin;
  
  addSectionTitle('6. PROJECT TIMELINE', '');

  const timeline = roadmapData.steps?.timeline || {};

  if (timeline.start_date && timeline.end_date) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Project Duration', margin, yPosition);
    yPosition += 7;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(`Start: ${timeline.start_date} | End: ${timeline.end_date}`, margin + 5, yPosition);
    
    const totalDays = differenceInDays(parseISO(timeline.end_date), parseISO(timeline.start_date));
    doc.text(`Total Duration: ${totalDays} days`, margin + 100, yPosition);
    yPosition += 12;
  }

  // Gantt Chart
  if (timeline.phases && timeline.phases.length > 0) {
    checkAndAddPage(100);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Gantt Chart View', margin, yPosition);
    yPosition += 10;

    yPosition = drawTimelineGantt(timeline.phases, timeline.start_date, timeline.end_date, yPosition);

    // Phase details
    yPosition += 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Phase Details', margin, yPosition);
    yPosition += 8;

    timeline.phases.forEach((phase, index) => {
      checkAndAddPage(30);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`Phase ${index + 1}: ${phase.name || 'Untitled'}`, margin + 3, yPosition);
      yPosition += 6;

      if (phase.description) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        const phaseDesc = doc.splitTextToSize(phase.description, pageWidth - 2 * margin - 6);
        phaseDesc.slice(0, 2).forEach(line => {
          checkAndAddPage();
          doc.text(line, margin + 3, yPosition);
          yPosition += 4;
        });
      }

      // Status badge
      if (phase.status) {
        const statusColors = {
          'not-started': colors.grey,
          'in-progress': colors.info,
          'completed': colors.success,
          'delayed': colors.error,
          'on-hold': colors.warning
        };
        const statusColor = statusColors[phase.status] || colors.grey;
        
        doc.setFillColor(...statusColor);
        doc.roundedRect(margin + 3, yPosition, 25, 5, 1, 1, 'F');
        doc.setFontSize(7);
        doc.setTextColor(...colors.white);
        doc.text(phase.status.toUpperCase(), margin + 4, yPosition + 3.5);
        yPosition += 8;
      }

      // Progress bar
      if (phase.progress !== undefined) {
        drawProgressBar(margin + 3, yPosition, 80, 5, phase.progress, true);
        yPosition += 10;
      }

      yPosition += 5;
    });
  }

  addFooter();

  // ===== REMAINING SECTIONS (Compact) =====

  // Section 4: Action Components
  doc.addPage();
  yPosition = margin;
  addSectionTitle('4. ACTION COMPONENTS', '');
  const components = roadmapData.steps?.action_components?.components || [];
  components.forEach((comp, idx) => {
    checkAndAddPage(15);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${idx + 1}. ${comp.name || 'Component'}`, margin + 3, yPosition);
    yPosition += 6;
    if (comp.description) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const lines = doc.splitTextToSize(comp.description, pageWidth - 2 * margin - 6);
      lines.slice(0, 2).forEach(line => {
        checkAndAddPage();
        doc.text(line, margin + 5, yPosition);
        yPosition += 4;
      });
    }
    yPosition += 6;
  });
  addFooter();

  // Section 5: Resources
  doc.addPage();
  yPosition = margin;
  addSectionTitle('5. RESOURCES', '');
  const resources = roadmapData.steps?.resources || {};
  
  if (resources.human_resources && resources.human_resources.length > 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Human Resources', margin, yPosition);
    yPosition += 7;
    
    resources.human_resources.slice(0, 5).forEach(hr => {
      checkAndAddPage(10);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const hrText = `${hr.name || 'Resource'}${hr.role ? ' - ' + hr.role : ''}`;
      doc.text(hrText, margin + 8, yPosition);
      yPosition += 5;
    });
    yPosition += 5;
  }

  if (resources.financial_resources && resources.financial_resources.length > 0) {
    checkAndAddPage(20);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Financial Resources', margin, yPosition);
    yPosition += 7;
    
    const totalBudget = resources.financial_resources.reduce((sum, fr) => sum + (Number(fr.amount) || 0), 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Budget: $${totalBudget.toLocaleString()}`, margin + 5, yPosition);
    yPosition += 10;
  }
  addFooter();

  // Section 7: Risk Management
  doc.addPage();
  yPosition = margin;
  addSectionTitle('7. RISK MANAGEMENT', '');
  const risks = roadmapData.steps?.risks?.risk_items || [];
  
  risks.forEach((risk, idx) => {
    checkAndAddPage(20);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${idx + 1}. ${risk.name || 'Risk'}`, margin + 3, yPosition);
    yPosition += 6;
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    if (risk.likelihood || risk.impact) {
      const likelihoodColors = { low: colors.success, medium: colors.warning, high: colors.error };
      doc.setTextColor(...(likelihoodColors[risk.likelihood] || colors.grey));
      doc.text(`Likelihood: ${risk.likelihood || 'N/A'}`, margin + 5, yPosition);
      doc.setTextColor(...(likelihoodColors[risk.impact] || colors.grey));
      doc.text(`Impact: ${risk.impact || 'N/A'}`, margin + 50, yPosition);
      yPosition += 5;
    }
    
    if (risk.mitigation) {
      doc.setTextColor(0, 0, 0);
      doc.text(`Mitigation: ${risk.mitigation}`, margin + 5, yPosition);
      yPosition += 5;
    }
    yPosition += 6;
  });
  addFooter();

  // Section 8: Governance
  doc.addPage();
  yPosition = margin;
  addSectionTitle('8. GOVERNANCE', '');
  const governance = roadmapData.steps?.governance || {};
  
  if (governance.decision_makers && governance.decision_makers.length > 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Decision Makers', margin, yPosition);
    yPosition += 7;
    
    governance.decision_makers.forEach(dm => {
      checkAndAddPage(10);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const dmText = `${dm.name || 'Decision Maker'}${dm.role ? ' - ' + dm.role : ''}`;
      doc.text(dmText, margin + 8, yPosition);
      yPosition += 5;
    });
  }
  addFooter();

  // Section 9: Performance Metrics
  doc.addPage();
  yPosition = margin;
  addSectionTitle('9. PERFORMANCE METRICS', '');
  const performance = roadmapData.steps?.performance || {};
  const kpis = performance.kpis || [];
  
  kpis.forEach((kpi, idx) => {
    checkAndAddPage(20);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${idx + 1}. ${kpi.name || 'KPI'}`, margin + 3, yPosition);
    yPosition += 6;
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    if (kpi.target !== undefined) {
      doc.text(`Target: ${kpi.target}`, margin + 5, yPosition);
      yPosition += 5;
    }
    if (kpi.current !== undefined) {
      doc.text(`Current: ${kpi.current}`, margin + 5, yPosition);
      yPosition += 5;
    }
    yPosition += 6;
  });
  addFooter();

  // Section 10: Adaptation Strategy
  doc.addPage();
  yPosition = margin;
  addSectionTitle('10. ADAPTATION STRATEGY', '');
  const adaptation = roadmapData.steps?.adaptation || {};
  
  if (adaptation.reassessment_points && adaptation.reassessment_points.length > 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Reassessment Points', margin, yPosition);
    yPosition += 7;
    
    adaptation.reassessment_points.forEach(point => {
      checkAndAddPage(10);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const pointText = `${point.milestone || 'Checkpoint'}${point.date ? ' - ' + point.date : ''}`;
      doc.text(pointText, margin + 8, yPosition);
      yPosition += 5;
    });
  }
  
  if (adaptation.pivot_guidelines) {
    checkAndAddPage(20);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Pivot Guidelines', margin, yPosition);
    yPosition += 7;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    const guidelinesLines = doc.splitTextToSize(adaptation.pivot_guidelines, pageWidth - 2 * margin - 5);
    guidelinesLines.slice(0, 5).forEach(line => {
      checkAndAddPage();
      doc.text(line, margin + 5, yPosition);
      yPosition += 5;
    });
  }
  addFooter();

  // Save the PDF
  const fileName = `${roadmapData.project_name || 'Roadmap'}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
